import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  downloadCreditDisputeEvidence,
  type LedgerEntryType,
  useGetCustomerCreditLedger,
} from "@/api/customer-credit";
import { useSession } from "@/services/auth0/session";
import { formatCurrency } from "@/lib/format";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  FileDown,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

interface CreditHistoryDialogProps {
  customerId: string;
  customerName: string;
  /** Wallet frozen (deposit-charge dispute open) — shows the warning +
   * evidence download. */
  frozen?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_META: Record<
  LedgerEntryType,
  { label: string; direction: "in" | "out" | "neutral" }
> = {
  DEPOSIT: { label: "Deposit", direction: "in" },
  MANUAL_DEPOSIT: { label: "Manual deposit", direction: "in" },
  REFUND: { label: "Refund", direction: "in" },
  ADMIN_ADJUSTMENT: { label: "Adjustment", direction: "neutral" },
  ORDER_PAYMENT: { label: "Order payment", direction: "out" },
  DEPOSIT_REVERSAL: { label: "Chargeback", direction: "out" },
  MIGRATION: { label: "Migrated", direction: "neutral" },
};

export function CreditHistoryDialog({
  customerId,
  customerName,
  frozen,
  open,
  onOpenChange,
}: CreditHistoryDialogProps) {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetCustomerCreditLedger(customerId);
  const { token } = useSession();

  // balanceCents is the live total repeated on every page; entries accumulate.
  const balanceCents = data?.pages[0]?.balanceCents;
  const entries = data?.pages.flatMap((page) => page.entries) ?? [];

  const handleEvidenceDownload = async () => {
    try {
      if (!token) return;
      await downloadCreditDisputeEvidence(customerId, token);
    } catch {
      toast.error("Failed to download dispute evidence");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4">
            <span>Credit History - {customerName}</span>
            {frozen && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleEvidenceDownload()}
              >
                <FileDown className="h-4 w-4 mr-1" />
                Dispute evidence
              </Button>
            )}
          </DialogTitle>
          <DialogDescription>
            Account-credit ledger
            {balanceCents !== undefined
              ? ` — balance ${formatCurrency(balanceCents)}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {frozen && (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              Wallet frozen — a deposit charge is under dispute. Credit spending
              is blocked until it resolves.
            </AlertDescription>
          </Alert>
        )}

        {balanceCents !== undefined && balanceCents < 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Negative balance: the customer spent credit whose funding deposit
              was charged back — they owe {formatCurrency(-balanceCents)}.
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 py-4">
              Failed to load the credit ledger. Please try again.
            </div>
          )}

          {data && entries.length === 0 && (
            <div className="text-sm text-gray-500 py-8 text-center">
              No credit activity yet
            </div>
          )}

          {entries.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance After</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => {
                  const meta = TYPE_META[entry.type] ?? {
                    label: entry.type,
                    direction: "neutral" as const,
                  };
                  const moneyIn = entry.amountCents > 0;
                  return (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">
                        {format(new Date(entry.createdAt), "MMM d, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            entry.type === "DEPOSIT_REVERSAL"
                              ? "destructive"
                              : meta.direction === "in"
                                ? "default"
                                : "secondary"
                          }
                          className="flex items-center gap-1 w-fit"
                        >
                          {moneyIn ? (
                            <ArrowDownCircle className="h-3 w-3" />
                          ) : (
                            <ArrowUpCircle className="h-3 w-3" />
                          )}
                          {entry.manualDepositMethod === "CASH"
                            ? "Cash deposit"
                            : entry.manualDepositMethod === "E_TRANSFER"
                              ? "E-transfer deposit"
                              : meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-xs truncate">
                        {entry.reason}
                        {entry.reference && (
                          <div className="text-xs text-muted-foreground">
                            Reference: {entry.reference}
                          </div>
                        )}
                        {entry.orderId && (
                          <span className="text-gray-400">
                            {" "}
                            · order {entry.orderId.slice(-6)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium ${
                          moneyIn ? "text-green-600" : "text-gray-600"
                        }`}
                      >
                        {moneyIn ? "+" : ""}
                        {formatCurrency(entry.amountCents)}
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium ${
                          entry.balanceAfterCents < 0 ? "text-red-600" : ""
                        }`}
                      >
                        {formatCurrency(entry.balanceAfterCents)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {hasNextPage && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Load older entries
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
